import { useState } from 'react';
import PropTypes from 'prop-types';
import { useBoolean } from 'usehooks-ts';
import { FormControl, FormHelperText } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { DISPLAY_DATE_FORMAT, formatDate, toDate, toDayjs } from 'helper/DateLib';

import { DateTextField } from './DateTextField';

const styles = {
  wrapper: {
    position: 'relative',
    width: '100%',
    minWidth: 140,
    maxWidth: 355,
  },
  helperText: {
    marginTop: 1,
  },
};

const DatePicker = ({
  ref,
  disabled = false,
  onChange = () => {},
  customStyles = {},
  error = false,
  errorText = '',
  placeholder = '',
  value = null,
  minDate,
  maxDate,
  id,
  popperPlacement = 'bottom-start',
  onFocus = () => {},
  onBlur = () => {},
  name,
  ...props
}) => {
  const { value: isOpen, setTrue: openPicker, setFalse: closePicker, setValue: setIsOpen } = useBoolean(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const displayValue = formatDate(value, DISPLAY_DATE_FORMAT, '');

  const handleChange = (newValue) => {
    onChange(toDate(newValue));
  };

  const handleOpen = () => {
    openPicker();
    onFocus();
  };

  const handleClose = () => {
    closePicker();
    onBlur();
  };

  return (
    <FormControl fullWidth variant="standard" error={error} sx={[styles.wrapper, customStyles.wrapper]}>
      <DesktopDatePicker
        open={isOpen}
        disabled={disabled}
        format={DISPLAY_DATE_FORMAT}
        value={toDayjs(value)}
        minDate={toDayjs(minDate)}
        maxDate={toDayjs(maxDate)}
        slots={{ field: DateTextField }}
        slotProps={{
          field: {
            id,
            displayValue,
            placeholder,
            error,
            disabled,
            customStyles,
            setOpen: setIsOpen,
            setAnchorEl,
            inputRef: ref,
          },
          popper: {
            anchorEl,
            placement: popperPlacement,
          },
        }}
        onChange={handleChange}
        onClose={handleClose}
        onOpen={handleOpen}
        {...props}
      />
      {errorText && <FormHelperText sx={[styles.helperText, customStyles.helperText]}>{errorText}</FormHelperText>}
    </FormControl>
  );
};

DatePicker.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  customStyles: PropTypes.shape({
    wrapper: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    field: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    text: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    helperText: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  }),
  error: PropTypes.bool,
  errorText: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  id: PropTypes.string,
  popperPlacement: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
};
export default DatePicker;
